import React, { useState, useEffect } from 'react';
import useServicesService from '../../services/useServicesService';
import useCompaniesService from '../../services/useCompaniesService';
import BaseSelect from '../../components/Base/BaseSelect';
import BaseDatePicker from '../../components/Base/BaseDatePicker';
import './PaymentStatusPage.css';

interface PaymentStatusForm {
  company_id: string;
  start_date: string;
  end_date: string;
}

interface PreviewServiceStandard {
  id: number;
  date: string;
  folio: string;
  description: string;
  passengers: string;
  cost_center: string;
  requester?: string;
  authorizer: string;
  value: number;
  observaciones?: string;
  usuario_que_creo?: string;
}

interface PreviewServiceElAbra {
  id: number;
  columns: Record<string, string | number>;
}

interface PreviewData {
  format?: 'el_abra' | 'standard';
  company: string;
  start_date: string;
  end_date: string;
  total_services: number;
  headers?: string[];
  services: Array<PreviewServiceStandard | PreviewServiceElAbra>;
  cost_center_summary: Array<{
    code: string;
    total: number;
  }>;
  total_general?: number;
}

function isElAbraPreview(data: PreviewData): data is PreviewData & {
  format: 'el_abra';
  headers: string[];
  services: PreviewServiceElAbra[];
} {
  return data.format === 'el_abra' && Array.isArray(data.headers);
}

const PaymentStatusPage: React.FC = () => {
  const { getPaymentStatusPreview, downloadPaymentStatus, fetching } = useServicesService();
  const { list: listCompanies, fetching: fetchingCompanies } = useCompaniesService();
  
  const [form, setForm] = useState<PaymentStatusForm>({
    company_id: '',
    start_date: '',
    end_date: '',
  });
  
  const [companies, setCompanies] = useState<Array<{ value: string; label: string }>>([]);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    const response = await listCompanies();
    if (response?.data?.companies) {
      setCompanies(
        response.data.companies.map((c: any) => ({
          value: String(c.id),
          label: c.name,
        }))
      );
    }
  };

  const handleChange = (name: keyof PaymentStatusForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setPreviewData(null);
    setError('');
  };

  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleChange('company_id', e.target.value);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e.target.name as keyof PaymentStatusForm, e.target.value);
  };

  const handlePreview = async () => {
    if (!form.company_id || !form.start_date || !form.end_date) {
      setError('Por favor complete todos los campos');
      return;
    }

    if (new Date(form.start_date) > new Date(form.end_date)) {
      setError('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }

    setError('');
    const response = await getPaymentStatusPreview(
      Number(form.company_id),
      form.start_date,
      form.end_date,
      {
        onSuccess: (data) => {
          if (data?.data) {
            setPreviewData(data.data);
          }
        },
        onError: (err) => {
          setError(err.message || 'Error al obtener el preview');
        },
      }
    );
  };

  const handleDownload = async () => {
    if (!form.company_id || !form.start_date || !form.end_date) {
      setError('Por favor complete todos los campos');
      return;
    }

    if (new Date(form.start_date) > new Date(form.end_date)) {
      setError('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }

    setError('');
    await downloadPaymentStatus(
      Number(form.company_id),
      form.start_date,
      form.end_date,
      {
        onSuccess: () => {
          setError('');
        },
        onError: (err) => {
          setError(err.message || 'Error al descargar el archivo');
        },
      }
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="payment-status-page">
      <div className="payment-status-header">
        <h1>Estado de Pago</h1>
        <p>Genere y descargue el estado de pago en formato Excel</p>
      </div>

      <div className="payment-status-form">
        <div className="form-row">
          <div className="form-field">
            <BaseSelect
              label="Empresa"
              name="company_id"
              value={form.company_id}
              onChange={handleCompanyChange}
              options={companies}
              placeholder="Seleccionar empresa"
            />
          </div>

          <div className="form-field">
            <BaseDatePicker
              label="Fecha de Inicio"
              name="start_date"
              value={form.start_date}
              onChange={handleDateChange}
            />
          </div>

          <div className="form-field">
            <BaseDatePicker
              label="Fecha de Fin"
              name="end_date"
              value={form.end_date}
              onChange={handleDateChange}
            />
          </div>
        </div>

        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={fetching.paymentStatusPreview || fetchingCompanies.list}
            className="btn btn-preview"
          >
            {fetching.paymentStatusPreview ? 'Cargando...' : 'Vista Previa'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={fetching.paymentStatusDownload || !previewData}
            className="btn btn-download"
          >
            {fetching.paymentStatusDownload ? 'Descargando...' : 'Descargar Excel'}
          </button>
        </div>
      </div>

      {previewData && (
        <div className="preview-section">
          <h2>Vista Previa</h2>
          <div className="preview-info">
            <p><strong>Empresa:</strong> {previewData.company}</p>
            <p><strong>Período:</strong> {previewData.start_date} hasta {previewData.end_date}</p>
            <p><strong>Total de Servicios:</strong> {previewData.total_services}</p>
          </div>

          {previewData.services.length > 0 && (
            <div className="preview-table">
              <h3>
                {isElAbraPreview(previewData)
                  ? 'Planilla El Abra (primeros 100 servicios)'
                  : 'Planilla General (primeros 100 servicios)'}
              </h3>
              {isElAbraPreview(previewData) ? (
                <table>
                  <thead>
                    <tr>
                      {previewData.headers.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData.services as PreviewServiceElAbra[]).map((service, index) => (
                      <tr key={index}>
                        {previewData.headers.map((header) => (
                          <td key={header}>{service.columns[header] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>ID Servicio</th>
                      <th>Fecha</th>
                      <th>Folio</th>
                      <th>Descripción</th>
                      <th>Usuario</th>
                      <th>Centro Costo</th>
                      <th>Solicitante</th>
                      <th>Autorizador</th>
                      <th>Valor</th>
                      <th>Observaciones</th>
                      <th>Usuario que creó</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData.services as PreviewServiceStandard[]).map((service, index) => (
                      <tr key={index}>
                        <td>{service.id}</td>
                        <td>{new Date(service.date).toLocaleDateString('es-CL')}</td>
                        <td>{service.folio || ''}</td>
                        <td>{service.description}</td>
                        <td>{service.passengers}</td>
                        <td>{service.cost_center}</td>
                        <td>{service.requester || ''}</td>
                        <td>{service.authorizer}</td>
                        <td>{formatCurrency(service.value)}</td>
                        <td>{service.observaciones || ''}</td>
                        <td>{service.usuario_que_creo || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {previewData.cost_center_summary.length > 0 && (
            <div className="preview-summary">
              <h3>Resumen por Centro de Costo</h3>
              <table>
                <thead>
                  <tr>
                    <th>Centro de Costo</th>
                    <th>N.º servicios</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.cost_center_summary.map((item, index) => (
                    <tr key={index}>
                      <td>{item.code}</td>
                      <td>{item.total}</td>
                    </tr>
                  ))}
                  {typeof previewData.total_general === 'number' && previewData.total_general > 0 && (
                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                      <td>TOTAL GENERAL</td>
                      <td>{previewData.total_general}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentStatusPage;

